import React from "react";
import {
  LayoutWP,
  Container,
  Grid,
  Column,
  SocialShare,
  Link,
  List,
  PersonBadge,
  Breadcrumb,
  NewsletterForm,
} from "../../../src/components";
import { BlockResolver } from "../../../src/util/block-resolver";
import { formatDate } from "../../../src/util/date";
import { CleanDescription } from "../../../src/util/string-manipulation";
import { crumbsFromLocation } from "../../../src/util/breadcrumb-resolver";
import { localMediaUrl } from "../../../src/util/url-resolver";
import { defaultMetaImage } from "../../../src/util/meta-images";
import { decode } from "html-entities";
import "./styles/post.scss";

const BlogPost = ({ data, pageContext, ...props }) => {
  const currentPage = pageContext;
  const cleanExcerpt = currentPage.excerpt.replace(/(<([^>]+)>)/gi, "");
  const pageMeta = JSON.parse(currentPage.meta_fields_json);
  pageMeta.excerpt = cleanExcerpt;
  pageMeta.index_and_follow = pageMeta.index_and_follow
    ? pageMeta.index_and_follow
    : "true";
  const crumbs = crumbsFromLocation(props.location);

  const author =
    pageMeta.author && pageMeta.author.length > 0 ? pageMeta.author : null;
  const postDate = new Date(Date.parse(currentPage.date));

  const getByline = () => {
    let byline;
    if (author && pageMeta.author.length > 0) {
      byline = (
        <>
          <span>by </span>
          {pageMeta.author.map((author, index) => (
            <span key={`author_${author.slug}`}>
                <Link url={`/people/${author.post_name}`}><span dangerouslySetInnerHTML={{ __html: author.post_title }}/></Link>
                {index + 1 < pageMeta.author.length
                  ? index + 2 === pageMeta.author.length
                    ? " and "
                    : ", "
                  : null}
            </span>
          ))}
          <span className="pm-blog__byline__separator">|</span>
        </>
      );
    }
    return byline;
  };

  const getAutoMetaDescription = (blocks) => {
    for (const block of blocks) {
      if (block.fields_json) {
        let block_json = JSON.parse(block.fields_json);

        switch (block.blockName) {
          case 'acf/markdown':
            return CleanDescription(block_json.markdown);
        }
      }
    }
    return null;
  }

  const getAuthorBadges = () => {
    let badge;
    if (author && pageMeta.author.length > 0) {
      badge = (
      <>
        {pageMeta.author.map((author, index) => {
          const authorPhoto = author
          ? author.fields.headshot
            ? author.fields.headshot
            : { url: "/defaults/headshot.png", alt: "" }
          : null;
        return <PersonBadge
            key={`authorBadge_${author.slug}`}
            name={author.post_title}
            image={authorPhoto}
            url={`/people/${author.post_name}`}
            username={author.fields ? author.fields.github_username : ""}
            location={author.fields ? author.fields.location : ""}
          />
        })}
      </>
      )
    }
    return badge;
  };

  const getCategories = () => {
    let categories;
    if (currentPage.categories && currentPage.categories.length > 0) {
      categories = (
        <div className="pm-blog__categories">
          <span>See more posts in </span>
          {currentPage.categories.map((category, index) => (
            <span key={`category_${category.slug}`}>
              <Link url={`/blog/category/${category.slug}`}><span dangerouslySetInnerHTML={{ __html: category.name }}/></Link>
              {index + 1 < currentPage.categories.length
                ? index + 2 === currentPage.categories.length
                  ? " and "
                  : ", "
                : null}
            </span>
          ))}
        </div>
      );
    }
    return categories;
  };

  const getArticleTags = () => {
    if (currentPage.categories && currentPage.categories.length > 0) {
      return currentPage.categories.map((cat) => cat.name);
    }

    return {};
  }

  const getBlogSeries = () => {
    let list;
      let items = [];
    if (
      pageMeta.blog_series_navigation &&
      pageMeta.blog_series_navigation.length > 0
    ) {
      items = pageMeta.blog_series_navigation.map((seriesItem, index) => {
        if (seriesItem && seriesItem.post_name) {
          return {
            link: {
              url: `/blog/${seriesItem.post_name}`,
              text: seriesItem.post_title,
            },
          };
        } else {
          return null;
        }
      });
    }
    if (items.length > 0) {
      list = <List list={items} hasSeparators={true} />;
    }
    return list;
  };

  const getAuthors = () => {
    const getAuthorNames = () => {
      const authorMap = pageMeta.author.map((author, x) => {
        const authorSet =
        `{
          "@type": "Person",
          "name": "${pageMeta.author[x].post_title}"
        }`
        return authorSet;
      })
      return authorMap;
    };
    const authors =
      pageMeta.author.length >= 1 ?
      getAuthorNames() :
      `{
        "@type": "Organization",
        "name": "Puppet"
      }`
    return authors;
  };

  const getBlogPhoto = () => {
    const blogPhoto =
      pageMeta.og_image && pageMeta.og_image.url ?
      localMediaUrl(pageMeta.og_image.url) :
      pageMeta.author.length >= 1 && pageMeta.author[0].fields.headshot ?
      localMediaUrl(pageMeta.author[0].fields.headshot.url) :
      defaultMetaImage();
    return blogPhoto;
  };

  const schemaBlogPostJSON = `{
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": "${decode(currentPage.title)}",
    "image": "${getBlogPhoto()}",
    "datePublished": "${formatDate(postDate)}",
    "author": [
      ${getAuthors()}
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Puppet",
      "logo": {
        "@type": "ImageObject",
        "url": "${defaultMetaImage()}"
      }
    },
    "mainEntityofPage": "${props.location.href}"
  }`;

  pageMeta.tags = getArticleTags();

  return (
    <LayoutWP
      canonicalUrl={pageContext.uri}
      pageTitle={currentPage.title}
      pageMeta={pageMeta}
      schemaOrg={schemaBlogPostJSON}
      location={props.location}
      className="pm-blog"
      autoMetaDescription={getAutoMetaDescription(JSON.parse(currentPage.blocks_json))}
    >
      <Container>
        <Breadcrumb breadcrumbs={crumbs}/>
      </Container>
      <article className="pm-blog__content">
        <Container pageWidth={true}>
          <Grid hasGutter={false}>
            <Column aboveSm={{ span: 8 }} aboveLg={{ span: 6, offset: 1 }}>
              <h1
                dangerouslySetInnerHTML={{ __html: currentPage.title }}
                className="pm-font-size--h2 pm-textColor--ogdarkpurple"
              />
              <div className="pm-blog__byline">
                {getByline()}
                <span className="pm-blog__byline__date">
                  {formatDate(postDate)}
                </span>
              </div>
              <div className="pm-blog__body">
                <BlockResolver blocks={JSON.parse(currentPage.blocks_json)} />
                <div className="trd-ph-embedded" data-group="General_rec"></div>
              </div>
            </Column>
            <Column
              aboveSm={{ span: 4 }}
              aboveMd={{ span: 3, offset: 9 }}
              >
              <aside className="pm-blog__sidebar">
                {getAuthorBadges()}
                <div className="pm-blog__sidebar__series">
                  {pageMeta.blog_series_title ? (
                    <h5>{pageMeta.blog_series_title}</h5>
                  ) : (
                    ""
                  )}
                  {getBlogSeries()}
                </div>
                { getCategories() }
                <SocialShare type="post" />
                <a className="pm-icon--rss" href="/blog/rss.xml">RSS Feed</a>
                <NewsletterForm />
              </aside>
            </Column>
          </Grid>
        </Container>
      </article>
    </LayoutWP>
  );
};

export default BlogPost;
